import { type ICellEditorParams } from "ag-grid-community";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { isBackspace, isPrintableKey } from "@/ui-modules/core/grid/cells/editors/utils.ts";

import { type Organism } from "../../../../../graphql.js";
import { searchGenomesForSamples } from "../../../../../redux/modules/sample-editing.js";
import { type ReduxDispatch } from "../../../../../redux/redux-utils.js";
import { GenomeSelector } from "../../../../genome-viewer/GenomeSelector.js";
import { type CellEditorParams } from "../../cell-editor-factory.js";
import { TableCell } from "../core/TableCell.js";

export const OrganismCellEditor = forwardRef(function OrganismCellEditor(
  props: ICellEditorParams<string> & CellEditorParams,
  ref,
) {
  const { value } = props;
  const dispatch = useDispatch<ReduxDispatch>();
  const isPrintableEditingKey = isPrintableKey(props.eventKey);
  const isBackspaceKey = isBackspace(props.eventKey);

  const [tag, setTag] = useState<Organism>(value);

  const refInput = useRef(null);

  const c = useCallback(() => {
    return {
      // the final tag to send to the grid, on completion of editing
      getValue(): Organism {
        return tag;
      },
    };
  }, [tag]);

  /* Component Editor Lifecycle methods */
  useImperativeHandle(ref, c);

  const onChange = (option: Organism) => {
    setTag(option);
    setTimeout(props.stopEditing, 0);
  };

  return (
    <TableCell editor>
      <GenomeSelector
        ref={refInput}
        selectedGenome={tag}
        onSearch={(searchTerm: string) => dispatch(searchGenomesForSamples(searchTerm, props.rowIndex))} // send rowIndex to endpoint to check for excluded org_ids
        onSelect={onChange}
        onFocus={(_event) => (isBackspaceKey ? setTag(null) : undefined)}
        selectProps={{
          isClearable: true,
          defaultInputValue: isPrintableEditingKey ? props.eventKey : "",
          openMenuOnFocus: true,
          autoFocus: true,
          onBlur: () => props.stopEditing(),
        }}
      />
    </TableCell>
  );
});
